<template>
  <div class="c-loader" />
</template>

<script lang="ts" setup>
import useModal from '@/services/useModal';
import useLocalization from '@/services/useLocalization';
import useExercisesDB from '@/services/useExercisesDB';
import { useOnline } from '@vueuse/core';
import useExercises from '@/services/useExercises';
import useGenre from '@/services/useGenre';
import { useAuth, useTenant } from '@/core';
import useMeta from '@/services/useMeta';
import useError, { ResponseErrorStatusCode, ErrorWithStatus } from '@/services/useError';

interface LoaderProps {
  retryCount?: number;
  errorMessage?: string;
}

const props = withDefaults(defineProps<LoaderProps>(), {
  retryCount: 3,
  errorMessage: 'Can not fetch data!',
});

const emit = defineEmits<{
  loaded: [value: boolean];
}>();

const { locale } = useLocalization();
const { loadGenres } = useGenre();
const { close } = useModal();
const { database, replicationOptions } = useExercisesDB();
const online = useOnline();
const { loadAllExercises } = useExercises();
const { loadMetadata } = useMeta();

const { login } = useAuth();
const { tenant } = useTenant();
const { showErrorWith } = useError();

let count = 0;

const onLoadFinished = () => emit('loaded', true);

const loadingFunction = () => {
  if (count++ < props.retryCount) {
    Promise.all([
      loadMetadata().catch((error) => {
        console.error('Failed to load metadata', error);
      }),
      loadGenres(locale.value.substr(0, 2)),
      ...(online.value
        ? [database.initSync(replicationOptions()).then(() => loadAllExercises())]
        : [loadAllExercises()]),
    ])
      .then(() => {
        onLoadFinished();
        close();
      })
      .catch((error: ErrorWithStatus) => {
        const delay = Math.pow(10, count) * 1000;
        console.log(`Retrying in ${delay} milliseconds`);
        console.error(error);

        if (ResponseErrorStatusCode.UNAUTHORIZED == error.status?.toString()) {
          login(tenant.value.namespace);
          setTimeout(loadingFunction, delay);
        } else if (ResponseErrorStatusCode.FORBIDDEN == error.status?.toString()) {
          showErrorWith(ResponseErrorStatusCode.FORBIDDEN);
          onLoadFinished();
          close();
        } else {
          setTimeout(loadingFunction, delay);
        }
      });
  } else {
    console.error('err', props.errorMessage);
  }
};

loadingFunction();
</script>
